import { Button } from 'antd'
import { observer } from 'mobx-react-lite'
import { FaFlagCheckered } from 'react-icons/fa'

import { notificationService } from '@/Core'
import { crudResourceRegistry } from '@/Core/Services/CrudResourceRegistry'

import { CompleteActivityModal } from '../Components/Modals/CompleteActivityModal'
import { ACTIVITY_RESOURCE } from '../Contracts/Activity.types'
import { isActivityCompleted } from '../Helpers/activityOverdue'
import { CompleteActivityModalStore } from '../Stores/CompleteActivityModalStore'

import type { ActivityCrudContextLike, ActivityFormContextLike, ActivityRecord } from '../Contracts/Activity.types'

/** Параметри обробника `detailConfig.modeConfig.view.onButtons` ядра. */
export interface ActivityViewButtonsParams {
    buttons: Record<string, unknown>
    crudContext?: unknown
    content?: unknown[]
}

interface ButtonDeps {
    formContext?: ActivityFormContextLike | undefined
}

/**
 * Чи показувати «Завершити справу» (`SPEC-011` §8.5, З1): лише в перегляді
 * (у редагуванні незбережені правки губилися б — легасі перечитував запис) і
 * лише за `_permissions.complete` — бек дає його «Новій» справі тому, хто її
 * бачить (`SPEC-011` §10). Ролей фронт не питає.
 */
export const canCompleteActivity = (deps: ButtonDeps): boolean => {
    const formContext = deps.formContext

    if (!formContext || formContext.isEditable === true) return false

    const values = formContext.values

    return values?._permissions?.complete === true && !isActivityCompleted(values)
}

/**
 * Що кнопка бере з картки — загорнуте в звичайний (не MobX) клас.
 *
 * ⚠️ Елемент `children` ядрова `ButtonGroup` кладе в глибоко спостережуваний
 * стор і віддає через `toJS`: спостережуваний об'єкт у пропсах вертався б
 * звідти копією без методів. Екземпляр звичайного класу MobX не клонує (той
 * самий прийом, що в кнопок верифікації CRM).
 */
class CompleteButtonSource {
    constructor(
        readonly store: CompleteActivityModalStore,
        readonly deps: ButtonDeps
    ) {}
}

/**
 * Кнопка «Завершити справу» — власна antd-кнопка, а не ядрова: ядровий
 * `Button` перестворює тип компонента на кожен рендер (HANDOFF, вада
 * `priority: 'dropdown'`), і на ньому ж зламане «⋮». Вигляд — як у кнопок
 * групи: клас `button-group-item`, розмір `middle`.
 */
const CompleteActivityButton = observer(({ source }: { source: CompleteButtonSource }) => {
    const { store, deps } = source

    if (!canCompleteActivity(deps)) return null

    return (
        <Button
            type="primary"
            size="middle"
            className="button-group-item"
            icon={<FaFlagCheckered />}
            onClick={() => store.openFor(deps.formContext?.values)}
        >
            Завершити справу
        </Button>
    )
})

/**
 * Нова версія картки — з відповіді `complete`, якщо в ній є `_permissions`
 * (кнопка має зникнути одразу); інакше картку перечитуємо.
 */
const applyCard = async (crudContext: ActivityCrudContextLike, card: ActivityRecord | null): Promise<void> => {
    const form = crudContext.formContext
    const permissions = card?._permissions

    if (card && permissions && typeof permissions === 'object' && form?.setInitialValues) {
        form.setInitialValues(card)
        form.setStorage?.('responseData', { ...card })
        return
    }

    try {
        await crudContext.actions?.reloadData?.()
    } catch {
        // Справу могли видалити — лишається те, що на екрані, і повідомлення
    }
}

/**
 * Список під шухлядою: рядок статусів, червоні рядки й календар (він
 * перечитується після кожного завантаження списку) мають побачити нову
 * «Завершену».
 */
const reloadList = (crudContext: ActivityCrudContextLike): void => {
    const load = crudContext.listCrudContext?.listContext?.actions?.load

    if (typeof load === 'function') {
        load(true)
        return
    }

    crudResourceRegistry.reloadListIfExists(crudContext.resource?.key ?? ACTIVITY_RESOURCE)
}

/** Модалка поруч із шухлядою — через `content` ядра. */
const CompleteActivityDialog = ({
    store,
    crudContext,
}: {
    store: CompleteActivityModalStore
    crudContext: ActivityCrudContextLike
}) => (
    <CompleteActivityModal
        store={store}
        onConfirm={() => {
            void store.confirm().then(async (outcome) => {
                if (outcome.kind === 'done') {
                    notificationService.showSuccess('Готово', 'Справу завершено.')
                    await applyCard(crudContext, outcome.card)
                    reloadList(crudContext)
                    return
                }

                // Застаріла картка (уже завершили, змінилися права, видалили) —
                // модалку стор закрив, тут лише пояснення й свіжий стан
                if (outcome.kind === 'failed' && outcome.stale) {
                    notificationService.showError('Помилка', outcome.message)
                    await applyCard(crudContext, null)
                    reloadList(crudContext)
                }
            })
        }}
    />
)

const stores = new WeakMap<object, CompleteActivityModalStore>()

/**
 * Стор модалки — один на ядровий `crudContext`: ядро кличе `onButtons` на
 * кожен рендер, а кнопка й модалка мають ділити той самий стан.
 */
const completeStoreFor = (crudContext: object): CompleteActivityModalStore => {
    let store = stores.get(crudContext)

    if (!store) {
        store = new CompleteActivityModalStore()
        stores.set(crudContext, store)
    }

    return store
}

/**
 * Кнопка «Завершити справу» в шапці картки справи (`SPEC-011` §8.5) і
 * модалка результату. Підключення — у ресурсі:
 * `detailConfig.modeConfig.view.onButtons = ActivityViewButtons`.
 *
 * Лежить у `Hooks/` разом з `OwnerActivitiesTab`: дзвіночок і вкладки
 * власника відкривають картку справи ключем ресурсу, тож кнопка мусить
 * приходити з самим ресурсом.
 */
export const ActivityViewButtons = ({ buttons, crudContext, content }: ActivityViewButtonsParams): void => {
    if (crudContext === null || typeof crudContext !== 'object') return

    const context = crudContext as ActivityCrudContextLike
    const store = completeStoreFor(crudContext)
    const depStore: ButtonDeps = { formContext: context.formContext }

    // Ліворуч від «Редагувати» (sort 1000): головна дія над «Новою» справою
    buttons['completeActivity'] = {
        priority: 'button',
        sort: 500,
        depStore,
        depFn: (deps: ButtonDeps) => canCompleteActivity(deps),
        children: <CompleteActivityButton key="activity-complete" source={new CompleteButtonSource(store, depStore)} />,
    }

    content?.push(<CompleteActivityDialog key="activity-complete-dialog" store={store} crudContext={context} />)
}
